
import React from 'react';
import { Lock as LockIcon, FileText, Link as LinkIcon, Clock, DollarSign, Folder, MessageCircle, Briefcase } from 'lucide-react';
import { LockConfig, LockType } from '../types';
import { Button } from './ui/Button';

interface LockPreviewProps {
  title: string;
  type: LockType;
  imageUrl: string;
  buttonText: string;
  config: LockConfig;
  socialEnabled: boolean;
}

export const LockPreview: React.FC<LockPreviewProps> = ({
  title,
  type,
  imageUrl,
  buttonText,
  config,
  socialEnabled
}) => {
  const bgImage = imageUrl || 'https://images.unsplash.com/photo-1550684848-fac1c5b4e853?q=80&w=2070';
  const isBlurred = config?.preview?.blurStrength !== 'none';
  const isFolder = config?.files?.isFolder;
  const showFomo = config?.fomo?.enabled;
  const showTips = config?.tip?.enabled;
  const isB2B = config?.security?.b2bOnly;

  return (
    <div className="mx-auto w-[280px] h-[550px] bg-slate-900 rounded-[40px] p-3 shadow-2xl border-8 border-slate-800 relative overflow-hidden ring-1 ring-slate-900/50 transform transition-all hover:scale-[1.02]">
      {/* Notch */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-6 bg-slate-800 rounded-b-xl z-50"></div>
      
      {/* Screen Content */}
      <div className="w-full h-full bg-slate-900 rounded-[32px] overflow-hidden relative flex flex-col font-sans">
        
        {/* Background Layer */}
        <div className="absolute inset-0 z-0 bg-slate-800" /> {/* Fallback Color */}
        <div 
            className="absolute inset-0 z-0 bg-cover bg-center transition-all duration-500"
            style={{ 
                backgroundImage: `url(${bgImage})`,
                opacity: 0.6,
                filter: isBlurred ? 'blur(4px)' : 'blur(0px)'
            }}
        />
        <div className="absolute inset-0 z-0 bg-slate-900/40 backdrop-blur-sm" />

        {/* Content Container (Bottom aligned) */}
        <div className="relative z-10 flex-1 flex flex-col justify-end pb-4">
            <div className="w-full bg-white/95 backdrop-blur-xl rounded-t-[24px] p-5 shadow-xl border-t border-white/50 animate-in slide-in-from-bottom duration-500">
                
                {/* Icon */}
                <div className="flex justify-center -mt-10 mb-3">
                    <div className="bg-white p-3 rounded-full shadow-xl ring-4 ring-black/5">
                        {isFolder ? (
                            <Folder className="h-6 w-6 text-blue-600" />
                        ) : type === LockType.FILE ? (
                            <FileText className="h-6 w-6 text-slate-900" />
                        ) : (
                            <LinkIcon className="h-6 w-6 text-slate-900" />
                        )}
                    </div>
                </div>

                {/* FOMO Badge - New Position (Centered under icon) */}
                {showFomo && (
                    <div className="flex justify-center mb-3">
                         <div className="bg-red-50 text-red-600 px-2.5 py-0.5 rounded-full text-[10px] font-bold border border-red-100 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {config.fomo?.text || 'Limited Time Offer'}
                        </div>
                    </div>
                )}

                {/* Title */}
                <div className="text-center mb-5">
                    <h3 className="text-base font-bold text-slate-900 leading-tight mb-1 line-clamp-2">
                        {title || "Your Title Here"}
                    </h3>
                    <p className="text-[10px] text-slate-500">
                        {socialEnabled ? "Action required to unlock" : "Enter email to unlock"}
                    </p>
                </div>

                {/* Logic Simulation */}
                <div className="space-y-3">
                    {/* 1. Tip Jar Preview */}
                    {showTips && (
                        <div className="p-2 bg-green-50 border border-green-100 rounded-xl flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="bg-green-100 p-1.5 rounded-full text-green-600">
                                    <DollarSign className="w-3 h-3" />
                                </div>
                                <div className="text-xs font-medium text-green-900">Tip to support</div>
                            </div>
                            <span className="text-xs font-bold text-green-700">${config.tip?.amount || 3}</span>
                        </div>
                    )}

                    {/* 2. Social Gate Preview */}
                    {socialEnabled ? (
                        <Button fullWidth className="h-10 text-xs bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/20">
                            Unlock & Comment <MessageCircle className="w-3 h-3 ml-1.5" />
                        </Button>
                    ) : (
                        /* 3. Standard Form Preview */
                        <>
                            <div className="relative">
                                <input 
                                    disabled
                                    placeholder={isB2B ? "name@company.com" : "name@email.com"}
                                    className={`w-full h-9 px-3 rounded-xl bg-slate-100 border text-xs text-slate-500 ${isB2B ? 'border-indigo-200 bg-indigo-50/50 placeholder-indigo-300' : 'border-transparent'}`}
                                />
                                {isB2B && (
                                    <div className="absolute right-2 top-1/2 -translate-y-1/2 text-indigo-500">
                                        <Briefcase className="w-3 h-3" />
                                    </div>
                                )}
                            </div>
                            <Button fullWidth className="h-10 text-xs shadow-lg shadow-slate-900/20">
                                {buttonText || "Unlock Access 🔓"}
                            </Button>
                        </>
                    )}
                    
                    <div className="flex justify-center gap-2 opacity-40 mt-2">
                         <div className="w-8 h-1 bg-slate-200 rounded-full"></div>
                    </div>
                </div>

            </div>
        </div>

        {/* Floating Labels for clarity */}
        <div className="absolute top-4 right-4 flex flex-col gap-1 items-end pointer-events-none">
             <div className="bg-black/50 backdrop-blur-md text-white text-[9px] px-2 py-1 rounded-full font-medium">
                 Live Preview
             </div>
        </div>
      </div>
    </div>
  );
};
